import {axiosInstance} from '@/http-common'
import {jwtDecode} from 'jwt-decode'

export function isJwtTokenFormatValid(token: unknown): token is string {
    return typeof token === 'string'
        && token !== ''
        && token !== 'undefined'
        && token !== 'null'
        && token.split('.').length === 3
}

export function getJwtToken(): string | null {
    const token = localStorage.getItem('jwt_token')

    if (!isJwtTokenFormatValid(token)) {
        if (token === 'undefined' || token === 'null') {
            localStorage.removeItem('jwt_token')
        }

        return null
    }

    return token
}

export function getDecodedJwtToken(): any | null {
    const token = getJwtToken()

    if (!token) {
        return null
    }

    try {
        return jwtDecode(token)
    } catch {
        localStorage.removeItem('jwt_token')
        return null
    }
}

/**
 * Get company from jwt token
 */
export function getCompanyFromJwt(): number|any {
    return getDecodedJwtToken()?.company ?? null
}

/**
 * Get user ID from jwt token
 */
export function getUserIdFromJwt(): number|any {
    return getDecodedJwtToken()?.userId ?? null
}

/**
 * Vehicles by companyId from decoded jwt token
 */
export async function getMyVehicles(): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/vehicles/${ getCompanyFromJwt() }`)
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Vehicles by companyId from decoded jwt token for remote DevExtreme grids.
 */
export async function getMyVehiclesGrid(gridParams: Record<string, any> = {}): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/vehicles/${ getCompanyFromJwt() }`, {
            params: gridParams
        })
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Helper function to make an API call.
 * @param url - The endpoint URL.
 * @returns - A promise that resolves with the response data or rejects with an error.
 */
async function makeApiCall(url: string): Promise<any> {
    return await axiosInstance.get(url);
}

/**
 * Fetches the Mercure JWT token for a specific company.
 *
 * This function constructs the URL for the JWT token based on the company ID extracted
 * from the JWT using the `getCompanyFromJwt` helper function. It then makes an API call
 * to retrieve the Mercure JWT token.
 *
 * @returns {Promise<string>} - A promise that resolves with the Mercure JWT token string.
 */
export async function getMercureJwtToken(): Promise<string> {
    const companyId = getCompanyFromJwt();
    if(!companyId) {
        return ''
    }
    const url = `/api/v1/mercure/jwt-token/${ companyId }`;
    const response = await makeApiCall(url);

    return response.data;
}

/**
 * Fetches unread notifications history for the company obtained from the JWT token.
 * @returns - A promise that resolves with the unread notifications history data or rejects with an error.
 */
export async function getMyUnReadNotificationsHistoryList(limit = 50): Promise<any> {
    const companyId = getCompanyFromJwt();
    if(!companyId) {
        return ''
    }
    const url = `/api/v1/notification-history/no-read/${ companyId }?limit=${limit}`;
    return makeApiCall(url);
}

export async function getCountMyUnReadNotificationsHistoryList(): Promise<any> {
    const companyId = getCompanyFromJwt();
    if(!companyId) {
        return ''
    }
    const url = `/api/v1/notification-history/no-read/${ companyId }?count=1`;
    return makeApiCall(url);
}

/**
 * Parameter value from jwt token by name
 * @param name
 */
export async function getParameterFromToken(name: string): Promise<any> {
    return new Promise((resolve, reject) => {
        const decodedToken: any = getDecodedJwtToken()
        if (!decodedToken?.parameters) {
            reject()
            return
        }

        const parameter: Array<any> = decodedToken.parameters
        const filtered: Array<any> = parameter.filter(p => p.name as string === `${ name }`)
        if (filtered.length)
            resolve(filtered[0].value)
        else
            reject()
    })
}

/**
 * Last vehicles position
 */
export async function getLastVehiclesPos(): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/data/last_position/all/${ getCompanyFromJwt() }`)
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Vehicle route
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getVehicleRoute(imei: bigint, dateFrom: string, dateTo: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>
        (`/api/v1/data/route/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * History summary
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getHistorySummary(imei: bigint, dateFrom: string, dateTo: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/history/summary/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * History daily summary
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getHistoryDailySummary(imei: bigint, dateFrom: string, dateTo: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/history/daily_summary/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * List of allowed reports for specified imei (device)
 * @param imei
 */
export async function getListOfAllowedReports(imei: bigint): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report_type/list/${ imei }`).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Events list report
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getEventsListReport(imei: bigint, dateFrom: string, dateTo: string, gridParams: Record<string, any> = {}): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report/events/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo,
                    ...gridParams
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Engine speed (RPM) report
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getEngineSpeedReport(imei: bigint, dateFrom: string, dateTo: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report/rpm/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

export async function getFuelReport(imei: bigint, dateFrom: string, dateTo: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report/fuel/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Voltage report
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getVoltageReport(imei: bigint, dateFrom: string, dateTo: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report/voltage/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Speed report
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getSpeedReport(imei: bigint, dateFrom: string, dateTo: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report/speed/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Ignition report
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getIgnitionReport(imei: bigint, dateFrom: string, dateTo: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report/ignition/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Border crossing report
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getBorderCrossingReport(imei: bigint, dateFrom: string, dateTo: string, gridParams: Record<string, any> = {}): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report/border_crossing/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo,
                    ...gridParams
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Weekly report
 * @param imei
 * @param dateFrom
 * @param dateTo
 */
export async function getWeeklyReport(imei: bigint, dateFrom: string, dateTo: string, gridParams: Record<string, any> = {}): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/report/weekly_report/${ imei }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo,
                    ...gridParams
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Statistics
 * @param dateFrom
 * @param dateTo
 */
export async function getStatistics(dateFrom: string, dateTo: string, gridParams: Record<string, any> = {}): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get<Array<any>>(`/api/v1/statistics/${ getCompanyFromJwt() }`, {
            params:
                {
                    date_from: dateFrom,
                    date_to: dateTo,
                    ...gridParams
                }
        }).then(response => {
            resolve(response)
        }).catch(response => {
            reject(response)
        })
    })
}

/**
 * Users for editing vehicle's driver
 */
export async function getUsers(): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get('/api/v1/users')
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Vehicle details (map)
 */
export async function getVehicleDetails(imei: bigint, segmentHours?: number, segmentStart?: number, segmentStop?: number): Promise<any> {
    return new Promise((resolve, reject) => {
        let params = {};
        
        // Nowy format z zakresem
        if (segmentStart !== undefined && segmentStop !== undefined) {
            params = { 
                segment_start: segmentStart, 
                segment_stop: segmentStop 
            };
        }
        // Stary format dla kompatybilności
        else if (segmentHours !== undefined) {
            params = { segment_hours: segmentHours };
        }
        
        axiosInstance.get(`/api/v1/data/route/last/${ imei }`, { params })
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Reverse geocoding
 * @param latLng
 */
export async function getReverseGeocode(latLng: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/data/reverse_geocode/${ latLng }`)
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Detailed reverse geocoding - returns full address object
 * @param latLng Coordinates in "latitude,longitude" format
 */
export async function getReverseGeocodeDetailed(latLng: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/data/reverse_geocode_detailed/${ latLng }`)
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Forward geocoding - converts address to coordinates
 * @param address Full address string (e.g., "Street, City, Country")
 */
export async function getGeocode(address: string): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get('/api/v1/data/geocode', {
            params: {
                address: address
            }
        })
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * SENT-GEO
 */
export async function getSentGeoList(): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/sentgeo/${ getCompanyFromJwt() }`)
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * SENT-GEO active
 */
export async function getSentGeoActive(): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/sentgeo/active/${ getCompanyFromJwt() }`)
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Tacho sources
 */
export async function getTachoSources(): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get('/api/v1/tacho/sources')
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

/**
 * Stored driver-activity of the whole company (grid dataset). Used to map a vehicle's
 * current card number to its driver id for the map info panel.
 * @param take
 */
export async function getTachoActivityDrivers(take = 1000): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/tacho/activity/drivers?skip=0&take=${ take }`)
            .then(response => resolve(response))
            .catch(response => reject(response))
    })
}

/**
 * Stored activity contract of one driver (days, timelines, and the server-computed 561/2006
 * `times` section consumed by the map info panel).
 * @param id driver id
 * @param days latest stored days to include (optional)
 */
export async function getTachoActivityDriver(id: number | string, days?: number): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/tacho/activity/driver/${ id }`, {
            params: days !== undefined ? { days } : {}
        })
            .then(response => resolve(response))
            .catch(response => reject(response))
    })
}

export async function getNotificationTypes(): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance
            .get(`/api/v1/notification-types/${getCompanyFromJwt()}`)
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject(response);
            });
    });
}

/**
 * Geofencing report
 * @param imei
 * @param dateFrom
 * @param dateTo
 * @param page
 * @param limit
 * @param zoneName
 * @param sortBy
 * @param sortOrder
 */
export async function getGeofencingReport(
    imei: bigint, 
    dateFrom: string, 
    dateTo: string, 
    page: number = 1, 
    limit: number = 20, 
    zoneName?: string, 
    sortBy: string = 'id', 
    sortOrder: string = 'desc'
): Promise<any> {
    return new Promise((resolve, reject) => {
        const params: any = {
            date_from: dateFrom,
            date_to: dateTo,
            page,
            limit,
            sort_by: sortBy,
            sort_order: sortOrder
        }
        
        if (zoneName && zoneName.length >= 3) {
            params.zone_name = zoneName
        }
        
        axiosInstance.get<Array<any>>(`/api/v1/report/geofencing/${imei}`, { params })
            .then(response => {
                resolve(response)
            })
            .catch(response => {
                reject(response)
            })
    })
}

export async function getClientRefreshApiToken(): Promise<any> {
    return new Promise((resolve, reject) => {
        axiosInstance.get(`/api/v1/client/token/current?companyId=${getCompanyFromJwt()}`)
            .then(response => {
                resolve(response)
            })
            .catch(error => {
                reject(error)
            })
    })
}
